import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Check, Shield } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SignupCheckout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const trialToken = searchParams.get('trial');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Redirect to trial signup if trial token is present
  useEffect(() => {
    if (trialToken) {
      navigate(`/app/signup?trial=${trialToken}`);
    }
  }, [trialToken, navigate]);

  const handleCheckout = async (e) => {
    e.preventDefault();
    setError('');
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/payments/signup-checkout`, {
        email: email.toLowerCase(),
        origin_url: window.location.origin
      });
      
      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Card Container */}
        <div 
          className="bg-white rounded-2xl p-8 sm:p-10"
          style={{
            boxShadow: '0 4px 24px -4px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)'
          }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-[1.75rem] font-semibold text-gray-900 tracking-tight">
              Get a meal plan that adapts as you progress
            </h1>
            <p className="text-gray-500 mt-3 text-[0.95rem] leading-relaxed">
              Follow the plan, log your weight, and check in weekly.<br className="hidden sm:block" />
              Your meals adjust over time.
            </p>
          </div>

          {/* Pricing - Single Option */}
          <div className="mb-8">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5 text-center">
              <p className="text-sm font-medium text-blue-600 mb-1">Start with a</p>
              <p className="text-3xl font-bold text-gray-900 mb-1">7-day free trial</p>
              <p className="text-gray-600">
                Then <span className="font-semibold">£27/month</span> if not cancelled
              </p>
            </div>
          </div>

          {/* What's Included */}
          <div className="mb-8">
            <p className="text-sm font-medium text-gray-700 mb-3">What's included:</p>
            <ul className="space-y-2.5">
              {[
                'Personalized meal plan',
                'Recipes scaled to your goals',
                'Weekly check-ins & adjustments',
                'Progress tracking'
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-gray-600">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-50 flex items-center justify-center">
                    <Check className="h-3 w-3 text-green-600" strokeWidth={3} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Email Form */}
          <form onSubmit={handleCheckout} className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder="Enter your email to get started"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 text-base border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl"
                required
              />
              <p className="text-xs text-gray-400 mt-1.5 ml-1">
                This will be your account email
              </p>
            </div>
            
            {error && (
              <p className="text-sm text-red-500 text-center py-1">{error}</p>
            )}
            
            <Button 
              type="submit" 
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-base font-medium rounded-xl transition-colors"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Redirecting to checkout...
                </>
              ) : (
                'Start free trial'
              )}
            </Button>
          </form>

          {/* Guarantee & Trust */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-center text-sm text-gray-500 mb-3">
              No charge today. Cancel anytime during your trial.
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
              <Shield className="h-3.5 w-3.5" />
              <span>Secure checkout via Stripe</span>
            </div>
          </div>
        </div>

        {/* Bottom note */}
        <p className="text-center text-sm text-gray-400 mt-6">
          Cancel anytime. No commitment.
        </p>
      </div>
    </div>
  );
}
